import { auditTime, merge, Observable, OperatorFunction, pipe, switchMap, tap } from 'rxjs';
import { LiveStatus } from '@shared/models';
import { endsToday } from '@shared/utils/days';
import { liveStatus, reconnecting } from './mercure-stream';

const LIVE_REFETCH_MS = 15_000;

/** Loads each range once; one ending today refetches on every (re)connect and on activity, at most every 15 s. */
export function liveReport<Params extends { to?: string }, Report>(
    fetch: (params: Params) => Observable<Report>,
    activity: Observable<unknown>,
    setLive: (live: LiveStatus) => void,
): OperatorFunction<Params, Report> {
    const watch = (params: Params) => {
        const refetches = activity.pipe(
            liveStatus(setLive),
            auditTime(LIVE_REFETCH_MS),
            switchMap(() => fetch(params)),
        );

        return reconnecting(() => merge(fetch(params), refetches));
    };

    return pipe(
        // a past range stays off; a live one switches itself on once its stream opens
        tap(() => setLive(null)),
        switchMap((params) => (endsToday(params.to) ? watch(params) : fetch(params))),
    );
}
